import currencyFormatter from '../../../utils/currencyFormatter'

/** Ano padrão enquanto o lab concentra dados de 2026. */
export const BUDGET_DASHBOARD_DEFAULT_ANO = 2026

/** Termos fixos do Glossário CFO (v2) — usar na UI e logs. */
export const BUDGET_TERMS = {
  ORCADO: 'Orçado',
  DISTRIBUIDO: 'Distribuído',
  PROGRAMADO: 'Programado',
  COMPROMETIDO: 'Comprometido',
  EMPENHADO: 'Empenhado / Liberado',
  PAGO: 'Pago',
  DISP_DISTRIBUIR: 'Disponível p/ distribuir',
  DISP_COMPROMETER: 'Disponível p/ comprometer',
  DISP_PAGAR: 'Disponível p/ pagar',
  PRE_COMPROMISSO: 'Pré-comprometido',
}

/**
 * Esteira completa no nível centro de custo (orcRubr nível 2).
 * Rubricas/subrubricas agregam só os estágios existentes nos itens — ver drill-down futuro.
 */
export const CC_ESTEIRA_STEPS = [
  { key: 'orcado', label: BUDGET_TERMS.ORCADO, shortLabel: 'Orçado', color: '#5664d2' },
  { key: 'distribuido', label: BUDGET_TERMS.DISTRIBUIDO, shortLabel: 'Distrib.', color: '#f1b44c' },
  { key: 'programado', label: BUDGET_TERMS.PROGRAMADO, shortLabel: 'Program.', color: '#909090' },
  { key: 'comprometido', label: BUDGET_TERMS.COMPROMETIDO, shortLabel: 'Comprom.', color: '#34c38f' },
  { key: 'liberado', label: 'Liberado', shortLabel: 'Liber.', color: '#50a5f1' },
  { key: 'pago', label: BUDGET_TERMS.PAGO, shortLabel: 'Pago', color: '#273351' },
]

export function dateInYear(dateStr, ano) {
  if (!dateStr || !ano) return false
  const dt = String(dateStr).slice(0, 10)
  return dt >= `${ano}-01-01` && dt <= `${ano}-12-31`
}

export function addDaysToDate(dateStr, days) {
  if (!dateStr) return null
  const d = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`)
  if (Number.isNaN(d.getTime())) return null
  d.setDate(d.getDate() + Number(days || 0))
  return d.toISOString().slice(0, 10)
}

/** Data de referência do compromisso — alinhado a orc_ccusto.php. */
export function contrFornEventoRefDate(row) {
  return addDaysToDate(row.contrFornEvento_dtPrev, row.contrFornEvento_pgtoPrazoDias)
}

export function resolveCcIdFromItemOuRubr(row) {
  if (row.orcItem_id) return Number(row.cc_id_item)
  if (row.orcRubr_id) return Number(row.cc_id_rubr)
  return Number(row.cc_id) || 0
}

export function applyCcTotals(map, ccId, valor) {
  if (!ccId) return
  map[ccId] = (map[ccId] || 0) + Number(valor || 0)
}

export function formatBudgetMoney(value) {
  return currencyFormatter.format(Number(value || 0))
}

export function hasBudgetDashboardFullAccess(user) {
  if (!user) return false
  const perm = Number(user.perm_id)
  return perm === 1 || perm === 2 || perm === 3
}

export function filterCentrosDeCustoForUser(centros, allowedCcIds) {
  if (!Array.isArray(centros)) return []
  if (allowedCcIds == null) return centros
  const allowed = new Set(allowedCcIds.map(Number))
  return centros.filter(cc => allowed.has(Number(cc.value)))
}

/** Σ orcDotAnual_vlrAtu por CC — termo UI: Distribuído. */
export function aggregateDistribuidoPorCc(dotAnualRows) {
  const byCc = {}
  for (const row of dotAnualRows || []) {
    const ccId = Number(row.cc_id)
    if (!ccId) continue
    byCc[ccId] = (byCc[ccId] || 0) + Number(row.orcDotAnual_vlrAtu || 0)
  }
  return byCc
}

/** Σ orcDotAnual_vlrIni por CC — termo UI: Orçado (dotação inicial dos itens). */
export function aggregateOrcadoPorCc(dotAnualRows) {
  const byCc = {}
  for (const row of dotAnualRows || []) {
    const ccId = Number(row.cc_id)
    if (!ccId) continue
    const vlrIni = row.orcDotAnual_vlrIni
    const fallback = row.orcDotAnual_vlrAtu
    const valor = Number(vlrIni != null && vlrIni !== '' ? vlrIni : fallback || 0)
    byCc[ccId] = (byCc[ccId] || 0) + valor
  }
  return byCc
}

export function aggregateProgramadoPorCc(orcDotRows, ano) {
  const byCc = {}
  for (const row of orcDotRows || []) {
    if (!dateInYear(row.orcDot_data, ano)) continue
    applyCcTotals(byCc, Number(row.cc_id), row.orcDot_valor)
  }
  return byCc
}

export function aggregateComprometidoPorCc(eventoRows, ano) {
  const byCc = {}
  for (const row of eventoRows || []) {
    if (Number(row.contrForn_ativo) !== 1) continue
    if (!dateInYear(contrFornEventoRefDate(row), ano)) continue
    applyCcTotals(byCc, Number(row.cc_id), row.contrFornEvento_vlr)
  }
  return byCc
}

export function aggregateLiberadoPorCc(bdroRows, ano) {
  const byCc = {}
  for (const row of bdroRows || []) {
    if (!dateInYear(row.bdro_dtVenc, ano)) continue
    applyCcTotals(byCc, resolveCcIdFromItemOuRubr(row), Math.abs(Number(row.bdro_vlr || 0)))
  }
  return byCc
}

export function aggregatePagoPorCc(movRows, ano) {
  const byCc = {}
  for (const row of movRows || []) {
    if (!dateInYear(row.mov_dtQuit, ano)) continue
    if (Number(row.mov_vlr || 0) >= 0) continue
    applyCcTotals(byCc, resolveCcIdFromItemOuRubr(row), Math.abs(Number(row.mov_vlr || 0)))
  }
  return byCc
}

export function mergeCcTotals(ccMap, ccId, field, totalsMap) {
  if (!ccMap[ccId]) return
  ccMap[ccId][field] = Number(totalsMap[ccId] || 0)
}

export function buildCcSummaries({
  centros,
  orcCCustoRows,
  distribuidoPorCc,
  orcadoPorCc,
  programadoPorCc,
  comprometidoPorCc,
  liberadoPorCc,
  pagoPorCc,
  selectedCcId,
}) {
  const ccMap = {}
  for (const cc of centros || []) {
    ccMap[Number(cc.value)] = {
      ccId: Number(cc.value),
      ccNome: cc.label,
      orcado: 0,
      distribuido: 0,
      programado: 0,
      comprometido: 0,
      liberado: 0,
      pago: 0,
      tetoCc: 0,
      disponivelDistribuir: 0,
    }
  }

  for (const row of orcCCustoRows || []) {
    const ccId = Number(row.orcRubr_id)
    if (!ccMap[ccId]) continue
    ccMap[ccId].tetoCc = Number(row.orcCCusto_vlr || 0)
  }

  for (const ccId of Object.keys(ccMap)) {
    const id = Number(ccId)
    mergeCcTotals(ccMap, id, 'orcado', orcadoPorCc)
    mergeCcTotals(ccMap, id, 'distribuido', distribuidoPorCc)
    mergeCcTotals(ccMap, id, 'programado', programadoPorCc)
    mergeCcTotals(ccMap, id, 'comprometido', comprometidoPorCc)
    mergeCcTotals(ccMap, id, 'liberado', liberadoPorCc)
    mergeCcTotals(ccMap, id, 'pago', pagoPorCc)
  }

  let summaries = Object.values(ccMap).map(item => {
    const referenciaTeto = item.tetoCc > 0 ? item.tetoCc : item.orcado
    return {
      ...item,
      disponivelDistribuir: referenciaTeto - item.distribuido,
    }
  })

  if (selectedCcId) {
    summaries = summaries.filter(s => s.ccId === Number(selectedCcId))
  }

  return summaries.sort((a, b) => a.ccNome.localeCompare(b.ccNome, 'pt-BR'))
}

export function buildVisaoGeralTotais(summaries) {
  return (summaries || []).reduce(
    (acc, row) => ({
      orcado: acc.orcado + row.orcado,
      distribuido: acc.distribuido + row.distribuido,
      disponivelDistribuir: acc.disponivelDistribuir + row.disponivelDistribuir,
    }),
    { orcado: 0, distribuido: 0, disponivelDistribuir: 0 },
  )
}

export function countItensPorCc(dotAnualRows) {
  const byCc = {}
  for (const row of dotAnualRows || []) {
    const ccId = Number(row.cc_id)
    if (!ccId) continue
    byCc[ccId] = (byCc[ccId] || 0) + 1
  }
  return byCc
}

export function pctDistribuido(orcado, distribuido) {
  if (!orcado || orcado <= 0) {
    return distribuido > 0 ? 100 : 0
  }
  return Math.round((distribuido / orcado) * 100)
}

/** Percentual de uma etapa da esteira em relação ao orçado. */
export function pctDoOrcado(orcado, valor) {
  const o = Number(orcado || 0)
  const v = Number(valor || 0)
  if (o <= 0) return v > 0 ? null : 0
  return Math.round((v / o) * 100)
}

/** Percentual de uma etapa em relação a outra etapa de referência. */
export function pctRelativoReferencia(referencia, valor) {
  const r = Number(referencia || 0)
  const v = Number(valor || 0)
  if (r <= 0) return v > 0 ? null : 0
  return Math.round((v / r) * 100)
}

export function getEsteiraStepByKey(key) {
  return CC_ESTEIRA_STEPS.find(step => step.key === key) || CC_ESTEIRA_STEPS[0]
}

/** Etapas exibidas nas barras dos cards (todas após orçado). */
export function getCardBarSteps() {
  return CC_ESTEIRA_STEPS.slice(1)
}

/** Disponível para comprometer = Orçado − Comprometido. */
export function computeDisponivel(orcado, comprometido) {
  return Number(orcado || 0) - Number(comprometido || 0)
}

/** Cores da barra de disponível — quanto maior o saldo, melhor. */
export function getDisponivelPctBarAppearance(pctDisponivel) {
  const p = Number(pctDisponivel) || 0
  if (p <= 0) return { progressColor: 'danger', pctColor: '#f46a6a' }
  if (p < 10) return { progressColor: 'danger', pctColor: '#f46a6a' }
  if (p < 25) return { progressColor: 'warning', pctColor: '#e08e0b' }
  if (p < 50) return { progressColor: 'warning', pctColor: '#f1b44c' }
  if (p < 75) return { progressColor: 'info', pctColor: '#50a5f1' }
  return { progressColor: 'success', pctColor: '#34c38f' }
}

/** Agrega totais da esteira a partir de summaries de CC. */
export function aggregateEsteiraTotais(summaries) {
  const totals = { disponivel: 0 }
  for (const step of CC_ESTEIRA_STEPS) {
    totals[step.key] = 0
  }
  for (const row of summaries || []) {
    for (const step of CC_ESTEIRA_STEPS) {
      totals[step.key] += Number(row[step.key] || 0)
    }
  }
  totals.disponivel = computeDisponivel(totals.orcado, totals.comprometido)
  return totals
}

/** Ordena cards da visão geral por campo numérico ou nome. */
export function sortBudgetCards(cards, sortBy, sortDir = 'asc') {
  const dir = sortDir === 'desc' ? -1 : 1
  return [...(cards || [])].sort((a, b) => {
    if (sortBy === 'cardNome') {
      return dir * String(a.cardNome || '').localeCompare(String(b.cardNome || ''), 'pt-BR')
    }
    const va = Number(a[sortBy] || 0)
    const vb = Number(b[sortBy] || 0)
    if (va !== vb) return dir * (va - vb)
    return String(a.cardNome || '').localeCompare(String(b.cardNome || ''), 'pt-BR')
  })
}

/**
 * Cores da barra conforme percentual consumido em relação à referência.
 * Quanto maior o %, mais crítico (verde → amarelo → vermelho).
 */
export function getEsteiraPctBarAppearance(pct, acimaDoTeto = false) {
  if (acimaDoTeto) {
    return { progressColor: 'danger', pctColor: '#f46a6a' }
  }
  const p = Number(pct) || 0
  if (p > 100) return { progressColor: 'danger', pctColor: '#f46a6a' }
  if (p === 100) return { progressColor: 'info', pctColor: '#5664d2' }
  if (p >= 90) return { progressColor: 'warning', pctColor: '#e08e0b' }
  if (p >= 75) return { progressColor: 'warning', pctColor: '#f1b44c' }
  if (p >= 50) return { progressColor: 'info', pctColor: '#50a5f1' }
  if (p > 0) return { progressColor: 'success', pctColor: '#34c38f' }
  return { progressColor: 'secondary', pctColor: '#adb5bd' }
}

export function buildContratosPorItem(contrFornRows) {
  const map = {}
  for (const row of contrFornRows || []) {
    const id = Number(row.orcItem_id)
    if (id) map[id] = (map[id] || 0) + 1
  }
  return map
}

export function hasCcEsteiraData(summary) {
  if (!summary) return false
  if (summary.qtdItens > 0) return true
  if (summary.tetoCc > 0) return true
  return CC_ESTEIRA_STEPS.some(step => Number(summary[step.key] || 0) !== 0)
}

export function enrichCcSummaries(summaries, dotAnualRows) {
  const itensPorCc = countItensPorCc(dotAnualRows)
  return (summaries || []).map(row => {
    const pct = pctDistribuido(row.orcado, row.distribuido)
    const estouro = row.tetoCc > 0
      ? row.distribuido > row.tetoCc
      : row.orcado > 0 && row.distribuido > row.orcado
    return {
      ...row,
      qtdItens: itensPorCc[row.ccId] || 0,
      pctDistribuido: pct,
      estouro,
    }
  })
}

export function buildDetalhamentoRows(dotAnualRows, selectedCcId, selectedOrcItemId = '') {
  let rows = (dotAnualRows || []).map(row => {
    const vlrIni = Number(row.orcDotAnual_vlrIni || 0)
    const vlrAtu = Number(row.orcDotAnual_vlrAtu || 0)
    return {
      id: row.orcDotAnual_id,
      orcItemId: Number(row.orcItem_id),
      ccId: Number(row.cc_id),
      ccNome: row.cc_nome || '',
      itemLabel: row.item_label || `Item #${row.orcItem_id}`,
      vlrIni,
      vlrAtu,
      desvio: vlrAtu - vlrIni,
    }
  })
  if (selectedCcId) {
    rows = rows.filter(r => r.ccId === Number(selectedCcId))
  }
  if (selectedOrcItemId) {
    rows = rows.filter(r => r.orcItemId === Number(selectedOrcItemId))
  }
  return rows.sort((a, b) => a.itemLabel.localeCompare(b.itemLabel, 'pt-BR'))
}
